import { useHint } from './useHint'
import './IconButton.css'

/**
 * Icon-only action button. The legacy row actions are exactly this: an icon with
 * the Arabic label carried as a tooltip, never as visible text.
 *
 * `label` is required — it is both the tooltip and the accessible name.
 *
 * THE TOOLTIP IS THE APP'S, not the browser's. `title` waits about a second, paints in
 * the operating system's font, ignores the page direction and never appears for a
 * keyboard user — which on a row of three grey glyphs left the labels effectively
 * missing, and "is this preview or delete?" a fair question. `Tooltip` answers at once,
 * on hover and on focus, in the app's own type. `aria-label` still carries the name, so
 * nothing depends on the bubble.
 *
 * @param {'brand'|'primary'|'success'|'info'|'danger'} [tone]
 * @param {'sm'|'md'} [size]
 * @param {boolean} [filled]  carry a surface at rest. For a control that stands
 *                            alone; a row of these inside a table is exactly the
 *                            noise the quiet default exists to avoid.
 * @param {import('react').ElementType} [as]  render as a link (`as={Link}`) when the
 *                                            action navigates rather than acts
 */
export default function IconButton({
  icon: Icon,
  label,
  tone = 'brand',
  size = 'sm',
  filled = false,
  as: Tag = 'button',
  className = '',
  ...rest
}) {
  const { hint, bubble } = useHint(label)

  const classes = [
    'icon-button',
    `icon-button--${tone}`,
    `icon-button--${size}`,
    filled && 'icon-button--filled',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <Tag
        className={classes}
        aria-label={label}
        {...hint}
        {...(Tag === 'button' ? { type: 'button' } : null)}
        {...rest}
      >
        <Icon size={size === 'sm' ? 16 : 18} strokeWidth={1.75} />
      </Tag>

      {bubble}
    </>
  )
}
